import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { FolderKanban, ListChecks, Users, Building2, ArrowUpRight, Clock, AlertTriangle } from "lucide-react";
import { useApp } from "../context/AppContext";
import { STATUS_STYLE, fmtDate } from "../lib/api";
import { Avatar, Card, Donut, Progress, StatusPill } from "../components/pm/bits";
import GoLiveCountdown, { useGoLive, WeeklyReportButton } from "../components/pm/GoLive";
import { QuickStatusButtons } from "../components/pm/StatusActions";

const Stat = ({ label, value, sub, icon: Icon, tone, testid }) => (
  <Card className="p-5 transition-shadow hover:shadow-md" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-slate-500">{label}</p>
        <p className="mt-1 text-3xl font-bold text-slate-900">{value}</p>
        <p className="mt-1 text-xs text-slate-400">{sub}</p>
      </div>
      <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ backgroundColor: tone.bg, color: tone.fg }}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </Card>
);

const StatusChart = ({ title, items, statuses }) => {
  const segs = statuses.map((s) => ({ label: s, value: items.filter((i) => i.status === s).length, color: STATUS_STYLE[s].dot })).filter((s) => s.value);
  return (
    <Card className="p-6">
      <h3 className="mb-4 text-base font-bold text-slate-900">{title}</h3>
      <div className="flex items-center gap-8">
        <Donut segments={segs} />
        <div className="flex-1 space-y-2.5">
          {segs.map((s) => (
            <div key={s.label} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-slate-600">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color }} /> {s.label}
              </span>
              <span className="font-bold text-slate-900">{s.value}</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

export default function Dashboard() {
  const { projects, tasks, dashboard, team, user, openTask } = useApp();
  const [golive, setGolive] = useGoLive();
  const epCount = team.filter((m) => (m.org || "EP Group") === "EP Group").length;
  const hCount = team.length - epCount;
  const nav = useNavigate();
  const d = dashboard || {};
  const perProject = [...projects].sort((a, b) => b.tasksTotal - a.tasksTotal).slice(0, 6);
  const max = perProject[0]?.tasksTotal || 1;
  const open = tasks.filter((t) => !["Completed", "Canceled"].includes(t.status));
  const upcoming = [...open].sort((a, b) => (a.dueDate || "9").localeCompare(b.dueDate || "9")).slice(0, 7);
  const myTasks = open.filter((t) => t.assigneeId === user?.id);
  const featured = [...projects].sort((a, b) => (b.status === "In-Progress") - (a.status === "In-Progress") || (a.startDate || "").localeCompare(b.startDate || "")).slice(0, 5);

  return (
    <div className="mx-auto max-w-7xl animate-fade-in-up">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">Programme Overview</h2>
          <p className="mt-1 text-sm text-slate-500">Live data from the Harrods Project Management System</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <WeeklyReportButton golive={golive} />
        {myTasks.length > 0 && (
          <Link to="/tasks?mine=1" className="flex items-center gap-2 rounded-lg border border-[#e7dcc6] bg-[#fbf8f1] px-3 py-2 text-sm font-medium text-[#6e5a32] hover:bg-[#f5efe3]" data-testid="my-tasks-link">
            You have <b>{myTasks.length}</b> open task{myTasks.length > 1 ? "s" : ""} <ArrowUpRight className="h-4 w-4" />
          </Link>
        )}
        </div>
      </div>

      <GoLiveCountdown g={golive} setG={setGolive} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testid="stat-projects" label="Projects" value={d.totalProjects ?? "–"} sub={`${d.inProgressProjects ?? 0} in progress`} icon={FolderKanban} tone={{ bg: "#e8f1ed", fg: "#00573f" }} />
        <Stat testid="stat-tasks" label="Open Tasks" value={d.openTasks ?? "–"} sub={`${d.totalTasks ?? 0} total · ${d.overdueTasks ?? 0} overdue`} icon={ListChecks} tone={{ bg: "#f5efe3", fg: "#a08450" }} />
        <Stat testid="stat-team" label="Team Members" value={d.teamCount ?? "–"} sub={`${epCount} EP Group · ${hCount} Harrods`} icon={Users} tone={{ bg: "#f3e8ff", fg: "#7e22ce" }} />
        <Stat testid="stat-customers" label="Customers" value={d.customerCount ?? "–"} sub="active accounts" icon={Building2} tone={{ bg: "#e0f2fe", fg: "#0369a1" }} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <StatusChart title="Projects by Status" items={projects} statuses={["Not Started", "In-Progress", "On Hold", "Completed", "Canceled"]} />
        <StatusChart title="Tasks by Status" items={tasks} statuses={["Not Started", "In-Progress", "Issue", "Completed", "Canceled"]} />
        <Card className="p-6">
          <h3 className="mb-4 text-base font-bold text-slate-900">Tasks per Project</h3>
          <div className="space-y-3.5">
            {perProject.map((p) => (
              <button key={p.id} onClick={() => nav(`/projects/${p.id}`)} className="block w-full text-left">
                <div className="mb-1 flex justify-between text-xs">
                  <span className="truncate font-medium text-slate-700 hover:text-[#00573f]">{p.name}</span>
                  <span className="text-slate-400">{p.tasksTotal}</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#00573f] to-[#2f7a5f]" style={{ width: `${(p.tasksTotal / max) * 100}%` }} />
                </div>
              </button>
            ))}
          </div>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-bold text-slate-900">Projects</h3>
            <Link to="/projects" className="flex items-center gap-1 text-sm font-semibold text-[#00573f] hover:underline">
              View all <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="space-y-2">
            {featured.map((p) => (
              <button
                key={p.id}
                onClick={() => nav(`/projects/${p.id}`)}
                className="flex w-full items-center gap-4 rounded-xl border border-slate-100 p-3 text-left transition-colors hover:border-[#e7dcc6] hover:bg-[#fbf8f1]"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-sm font-semibold text-slate-900">{p.name}</p>
                    <StatusPill status={p.status} />
                  </div>
                  <p className="mt-0.5 text-xs text-slate-400">{p.customer} · {p.tasksTotal} tasks · due {fmtDate(p.dueDate)}</p>
                </div>
                <div className="hidden w-40 sm:block">
                  <div className="mb-1 flex justify-between text-xs text-slate-500"><span>Progress</span><span>{p.progress}%</span></div>
                  <Progress value={p.progress} />
                </div>
                <Avatar person={p.pmAvatar} size={30} />
              </button>
            ))}
          </div>
        </Card>
        <Card className="p-6">
          <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-slate-900">
            <Clock className="h-[18px] w-[18px] text-slate-400" /> Upcoming Deadlines
          </h3>
          <div className="space-y-1">
            {upcoming.map((t) => (
              <div key={t.id} role="button" onClick={() => openTask(t.id)} className="flex w-full cursor-pointer items-center gap-3 rounded-lg p-2 text-left hover:bg-slate-50" data-testid={`deadline-${t.id}`}>
                <Avatar person={t.assigneeAvatar} size={28} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-800">{t.title}</p>
                  <p className={`flex items-center gap-1 text-xs ${t.overdue ? "text-red-600" : "text-slate-400"}`}>
                    {t.overdue && <AlertTriangle className="h-3 w-3" />} {fmtDate(t.dueDate)}
                  </p>
                </div>
                <QuickStatusButtons task={t} />
              </div>
            ))}
            {upcoming.length === 0 && <p className="text-sm text-slate-400">No open tasks.</p>}
          </div>
        </Card>
      </div>
      <p className="mt-8 text-center text-xs text-slate-400">{team.length} team members · Harrods programme</p>
    </div>
  );
}
